import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Shield, Bell, Settings, X, Moon, Sun, Volume2, VolumeX, UserCircle, LogOut, Monitor, Check } from 'lucide-react';
import { formatDate, formatTime, getGreeting, formatDateTime } from '../utils/dateUtils';
import { playAlertSound, installAudioUnlock } from '../utils/soundFx';
import { fetchUserPreferences, saveUserPreferences } from '../services/apiClient';

const SETTINGS_KEY = 'bondMentorUserSettings_v43e2';
const READ_NOTIFICATIONS_KEY = 'bondMentorReadNotifications_v43e2';
const HIDDEN_NOTIFICATIONS_KEY = 'bondMentorHiddenNotifications_v43g2';
const NOTIFICATION_HISTORY_KEY = 'bondMentorNotificationHistory_v43g2';

const DEFAULT_SETTINGS = {
  theme: 'dark',
  soundEnabled: false,
  desktopNotifications: true,
  signalChangeAlerts: true,
  dataDateFormat: 'MM-DD-YYYY',
  appDateFormat: 'MM-DD-YYYY',
  timeFormat: '12h',
  rememberCollapsedSections: true,
  missionControlLayout: 'expanded',
  showBeginnerNotes: true,
  compactAdminPanels: false,
};

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function readSettings() {
  return { ...DEFAULT_SETTINGS, ...readJson(SETTINGS_KEY, {}) };
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Browser storage can be unavailable in private mode. Preferences remain in memory.
  }
}

function rememberNotificationHistory(items) {
  const current = readJson(NOTIFICATION_HISTORY_KEY, []);
  const merged = new Map();
  [...items, ...current].forEach((item) => {
    if (!item?.id) return;
    merged.set(item.id, {
      id: item.id,
      time: item.time,
      type: item.type,
      msg: item.msg,
      color: item.color,
      severity: item.severity,
      archivedAt: item.archivedAt || null,
    });
  });
  const sorted = Array.from(merged.values())
    .sort((a, b) => safeDate(b.time).getTime() - safeDate(a.time).getTime())
    .slice(0, 75);
  writeJson(NOTIFICATION_HISTORY_KEY, sorted);
  return sorted;
}

function applyTheme(theme) {
  const root = document.documentElement;
  const prefersLight = window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches;
  const resolved = theme === 'system' ? (prefersLight ? 'light' : 'dark') : theme;
  root.dataset.theme = resolved;
  root.classList.toggle('theme-light', resolved === 'light');
  root.classList.toggle('theme-dark', resolved !== 'light');
  return resolved;
}

function useBondMentorSettings(user) {
  const [settings, setSettingsState] = useState(() => readSettings());
  const [savedFlash, setSavedFlash] = useState(false);
  const lastSyncedUserRef = useRef(null);
  const serverSaveTimerRef = useRef(null);

  useEffect(() => {
    applyTheme(settings.theme);
    document.documentElement.dataset.timeFormat = settings.timeFormat;
    document.documentElement.dataset.appDateFormat = settings.appDateFormat;
    document.documentElement.dataset.dataDateFormat = settings.dataDateFormat;
    document.documentElement.dataset.missionControlLayout = settings.missionControlLayout;
    writeJson(SETTINGS_KEY, settings);
    // Let other parts of the app (e.g. Mission Control sections) react live
    // when preferences change, without a page reload.
    try {
      window.dispatchEvent(new CustomEvent('bondmentor:settings', { detail: settings }));
    } catch {
      // CustomEvent is unavailable in some very old browsers; safe to ignore.
    }
  }, [settings]);

  useEffect(() => {
    const syncSystemTheme = () => {
      if (settings.theme === 'system') applyTheme('system');
    };
    const mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: light)') : null;
    if (mq?.addEventListener) mq.addEventListener('change', syncSystemTheme);
    return () => {
      if (mq?.removeEventListener) mq.removeEventListener('change', syncSystemTheme);
    };
  }, [settings.theme]);

  // DROP G — sync preferences with the account so they follow the user across
  // devices. localStorage stays the instant cache and offline/guest fallback.
  useEffect(() => {
    const userId = user?.id || null;
    if (!userId) { lastSyncedUserRef.current = null; return; }
    if (lastSyncedUserRef.current === userId) return; // already synced this login
    lastSyncedUserRef.current = userId;

    let cancelled = false;
    (async () => {
      const result = await fetchUserPreferences();
      if (cancelled || !result?.ok) return;
      if (result.hasStored && result.preferences) {
        // The account has saved preferences — apply them (cross-device sync).
        setSettingsState((prev) => ({ ...prev, ...result.preferences }));
      } else {
        // First login with no saved preferences — seed the account with this
        // device's current settings so they carry forward.
        saveUserPreferences(settings);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  function updateSetting(key, value) {
    setSettingsState((previous) => {
      const next = { ...previous, [key]: value };
      // Persist to the account (debounced) when logged in.
      if (user?.id) {
        window.clearTimeout(serverSaveTimerRef.current);
        serverSaveTimerRef.current = window.setTimeout(() => { saveUserPreferences(next); }, 600);
      }
      return next;
    });
    setSavedFlash(true);
    // Immediate, tangible confirmation that Alert Sounds actually works the
    // moment the user switches it on (this also unlocks audio for later alerts).
    if (key === 'soundEnabled' && value === true) {
      playAlertSound('preview', { force: true });
    }
    window.clearTimeout(window.__bondMentorSettingsFlashTimer);
    window.__bondMentorSettingsFlashTimer = window.setTimeout(() => setSavedFlash(false), 1400);
  }

  return { settings, updateSetting, savedFlash };
}

function safeDate(value) {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

function formatNotificationTime(value) {
  const now = new Date();
  const date = safeDate(value);
  const sameDay = now.toDateString() === date.toDateString();
  if (sameDay) return `Today · ${formatTime(date)}`;
  return formatDateTime(date);
}

function colorForSignal(signal) {
  const s = String(signal || '').toLowerCase();
  if (s.includes('red')) return '#ef4444';
  if (s.includes('orange')) return '#f97316';
  if (s.includes('yellow')) return '#eab308';
  return '#22c55e';
}

function buildLiveNotifications({ liveData, masterSignal, signalHistory, siteMode, user, auctionWatch }) {
  const notifications = [];
  const nowIso = new Date().toISOString();
  const signal = masterSignal?.signal || liveData?.signal?.signal;
  const score = Math.round(Number(masterSignal?.compositeScore ?? liveData?.signal?.compositeScore ?? 0));
  const computedAt = liveData?.signal?.computedAt || liveData?.fetchedAt || nowIso;
  const signalLabel = String(signal || 'UNKNOWN').replace(/_/g, ' ').toUpperCase();

  if (signal && Number.isFinite(score)) {
    notifications.push({
      id: `current-signal-${signalLabel}-${score}`,
      time: computedAt,
      type: 'Master Signal',
      msg: `Current reading is ${signalLabel} with a composite risk score of ${score}/100.`,
      color: colorForSignal(signal),
      severity: signalLabel,
      priority: 1,
    });
  }

  const freshness = Array.isArray(liveData?.dataFreshness) ? liveData.dataFreshness : [];
  const fallbackFeeds = freshness.filter((feed) => {
    const text = `${feed?.status || ''} ${feed?.source || ''} ${feed?.note || ''}`.toLowerCase();
    return text.includes('backup') || text.includes('fallback') || text.includes('estimated') || text.includes('configured');
  });
  if (fallbackFeeds.length) {
    notifications.push({
      id: `data-confidence-${fallbackFeeds.map((f) => f.key || f.label).join('-')}`,
      time: liveData?.fetchedAt || nowIso,
      type: 'Data Confidence',
      msg: `${fallbackFeeds.length} feed${fallbackFeeds.length === 1 ? '' : 's'} are using backup, estimated, or configured values. Check Data Freshness before making decisions.`,
      color: '#eab308',
      severity: 'Watch',
      priority: 2,
    });
  }

  if (siteMode?.effectiveMode && siteMode.effectiveMode !== 'live') {
    const label = siteMode.effectiveMode === 'maintenance' ? 'Maintenance' : 'Waitlist';
    notifications.push({
      id: `site-mode-${siteMode.effectiveMode}`,
      time: siteMode.updatedAt || nowIso,
      type: 'Site Mode',
      msg: `${label} mode is active${siteMode.source === 'env' ? ' from the .env override' : ''}.`,
      color: siteMode.effectiveMode === 'maintenance' ? '#ef4444' : '#eab308',
      severity: label,
      priority: 0,
    });
  }

  const history = Array.isArray(signalHistory) ? signalHistory.slice(0, 4) : [];
  history.forEach((entry, index) => {
    const entrySignal = String(entry.signal || 'Signal').toUpperCase();
    const entryScore = Math.round(Number(entry.score ?? 0));
    notifications.push({
      id: `history-${entry.date || index}-${entrySignal}-${entryScore}`,
      time: entry.date || nowIso,
      type: index === 0 ? 'Latest Confirmed' : 'Signal History',
      msg: `${entrySignal} confirmed at ${entryScore}/100${entry.action ? ` — ${entry.action}` : ''}.`,
      color: colorForSignal(entry.signal),
      severity: entry.status || 'Confirmed',
      priority: 3 + index,
    });
  });

  if (user?.permissions?.canAccessAdmin) {
    notifications.push({
      id: 'admin-tools-protected',
      time: nowIso,
      type: 'Admin',
      msg: 'Admin tools are protected by secure access and role checks.',
      color: '#3b82f6',
      severity: 'Protected',
      priority: 8,
    });
  }

  const auctionAlert = auctionWatch?.alert;
  if (auctionAlert?.active) {
    notifications.push({
      id: `auction-${auctionAlert.tone}-${auctionAlert.date}-${auctionAlert.grade}`,
      time: auctionAlert.date || nowIso,
      type: 'Treasury Auction',
      msg: auctionAlert.message,
      color: auctionAlert.tone === 'strong' ? '#22c55e' : '#ef4444',
      severity: auctionAlert.tone === 'strong' ? 'Strong' : 'Weak',
      priority: 2,
    });
  }

  if (!notifications.length) {
    notifications.push({
      id: 'no-alerts-yet',
      time: nowIso,
      type: 'System',
      msg: 'No live notifications are available yet. New signal, site mode, feedback, and data-confidence events will appear here.',
      color: '#3b82f6',
      severity: 'Ready',
      priority: 99,
    });
  }

  const unique = new Map();
  notifications
    .sort((a, b) => (a.priority || 99) - (b.priority || 99))
    .forEach((item) => {
      if (!unique.has(item.id)) unique.set(item.id, item);
    });

  return Array.from(unique.values()).slice(0, 8);
}

/* ============================================
   NOTIFICATION PANEL
   ============================================ */
function NotificationPanel({
  notifications,
  recentNotifications,
  hiddenIds,
  notificationView,
  flashMessage,
  onClose,
  onMarkAllRead,
  onClearFromView,
  onShowInbox,
  onShowRecent,
  onRestoreOne,
  onRestoreAll,
}) {
  const isRecent = notificationView === 'recent';
  const visibleItems = isRecent ? recentNotifications : notifications;
  const unread = notifications.filter((a) => !a.read).length;
  const hiddenCount = hiddenIds.length;

  const tabClass = (active) =>
    `notification-tab ${active ? 'is-active' : ''} rounded-lg border px-2 py-2 text-[10px] font-display font-bold transition ${active ? 'border-mentor-accent/80 bg-mentor-accent/15 text-mentor-accent shadow-sm shadow-blue-500/10' : 'border-mentor-border/70 text-mentor-text hover:border-mentor-accent/50 hover:text-mentor-accent'}`;

  return (
    <div className="notification-popover absolute right-0 top-full mt-2 w-[450px] max-w-[calc(100vw-2rem)] max-h-[600px] overflow-y-auto solid-popover shadow-2xl shadow-black/60 z-[100] border border-mentor-border/90" style={{ borderRadius: 16 }}>
      <div className="popover-header flex items-center justify-between p-4 border-b border-mentor-border/60 bg-slate-950/30">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-mentor-accent" />
          <h3 className="font-display font-bold text-sm text-mentor-text">Notifications</h3>
          {unread > 0 && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-signal-red text-white">{unread}</span>}
        </div>
        <button onClick={onClose} className="popover-close text-mentor-muted hover:text-mentor-text transition-colors"><X size={16} /></button>
      </div>

      <div className="notification-controls px-4 py-3 border-b border-mentor-border/50 bg-slate-950/20 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onShowInbox} className={tabClass(!isRecent)}>
            Inbox
          </button>
          <button type="button" onClick={onShowRecent} className={tabClass(isRecent)}>
            Recent History
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onMarkAllRead} className="notification-action rounded-lg border border-mentor-border/70 px-2 py-2 text-[10px] font-display font-bold text-mentor-text hover:border-mentor-accent/50 hover:text-mentor-accent transition">
            Mark All Read
          </button>
          <button type="button" onClick={onClearFromView} className="notification-action is-warning rounded-lg border border-mentor-border/70 px-2 py-2 text-[10px] font-display font-bold text-mentor-text hover:border-amber-400/60 hover:text-amber-300 transition">
            Clear Inbox
          </button>
        </div>
        {flashMessage && (
          <div className="notification-flash rounded-lg border border-mentor-accent/30 bg-mentor-accent/10 px-3 py-2 text-[11px] font-display font-bold text-mentor-accent">
            {flashMessage}
          </div>
        )}
        <p className="notification-help text-[10px] text-mentor-muted leading-relaxed font-body">
          Clearing only hides alerts from your Inbox. Recent History keeps a safety copy so you can restore cleared alerts.
        </p>
      </div>

      {isRecent && (
        <div className="recent-history-header px-4 py-3 border-b border-mentor-border/50 bg-mentor-accent/[0.06]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-display font-bold text-mentor-text">Recent Notification History</p>
              <p className="text-[10px] text-mentor-muted font-body">Recently shown alerts, including cleared items.</p>
            </div>
            {hiddenCount > 0 && (
              <button onClick={onRestoreAll} className="restore-all-btn shrink-0 rounded-lg border border-mentor-accent/40 bg-mentor-accent/10 px-3 py-2 text-[10px] font-display font-bold text-mentor-accent hover:bg-mentor-accent/15 transition">
                Restore All
              </button>
            )}
          </div>
        </div>
      )}

      <div className="p-2">
        {visibleItems.length === 0 ? (
          <div className="p-5 text-center rounded-xl border border-mentor-border/50 bg-slate-950/20">
            {isRecent ? (
              <>
                <p className="text-sm font-display font-bold text-mentor-text">No recent notification history yet.</p>
                <p className="text-xs text-mentor-muted mt-1">New signal, site mode, feedback, and data-confidence events will appear here after they are generated.</p>
              </>
            ) : (
              <>
                <p className="text-sm font-display font-bold text-mentor-text">Your notification inbox is clear.</p>
                <p className="text-xs text-mentor-muted mt-1">Cleared alerts can still be restored from Recent History.</p>
              </>
            )}
          </div>
        ) : visibleItems.map((a) => {
          const isHidden = hiddenIds.includes(a.id);
          return (
            <div key={a.id} className={`notification-row flex items-start gap-3 p-3 rounded-xl mb-1 transition-colors ${!a.read && !isRecent ? 'is-unread bg-mentor-accent/[0.10] border border-mentor-accent/10' : 'hover:bg-white/[0.035]'} ${isHidden ? 'is-hidden opacity-80' : ''}`}>
              <span className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: a.color, boxShadow: !a.read && !isRecent ? `0 0 9px ${a.color}80` : 'none' }} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${a.color}18`, color: a.color }}>{a.type}</span>
                  <span className="text-[10px] text-mentor-muted font-mono">{formatNotificationTime(a.time)}</span>
                  {!a.read && !isRecent && <span className="w-1.5 h-1.5 rounded-full bg-mentor-accent" />}
                  {isHidden && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-300">Cleared</span>}
                </div>
                <p className="text-xs font-body text-mentor-text leading-relaxed">{a.msg}</p>
                {isRecent && isHidden && (
                  <button type="button" onClick={() => onRestoreOne(a.id)} className="mt-2 text-[10px] font-display font-bold text-mentor-accent hover:text-blue-300 transition">
                    Restore to Inbox
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="popover-footer p-3 border-t border-mentor-border/60 bg-slate-950/25">
        <p className="text-[10px] text-mentor-muted text-center font-body">
          Manage alert preferences in Learn → Alerts.
        </p>
      </div>
    </div>
  );
}

function SettingToggle({ enabled, onChange, label, description, icon }) {
  return (
    <div className="setting-card flex items-center justify-between gap-4 rounded-xl border border-mentor-border/50 bg-slate-950/25 px-3 py-3">
      <div className="flex items-start gap-2">
        {icon}
        <div>
          <p className="text-sm font-display font-bold text-mentor-text">{label}</p>
          {description && <p className="text-[11px] text-mentor-muted mt-0.5 leading-relaxed">{description}</p>}
        </div>
      </div>
      <button onClick={() => onChange(!enabled)}
        className={`setting-toggle-switch ${enabled ? 'is-on' : 'is-off'} w-11 h-6 rounded-full transition-all relative flex-shrink-0`} style={{ background: enabled ? '#22c55e' : 'rgba(55,70,95,0.75)' }}>
        <div className="setting-toggle-knob w-5 h-5 rounded-full bg-white shadow absolute top-0.5 transition-all" style={{ left: enabled ? '22px' : '2px' }} />
      </button>
    </div>
  );
}

/* ============================================
   SETTINGS PANEL
   ============================================ */
function SettingsPanel({ onClose, settings, updateSetting, savedFlash, isLoggedIn }) {
  const themeOptions = [
    { key: 'dark', label: 'Dark', icon: <Moon size={14} /> },
    { key: 'light', label: 'Light', icon: <Sun size={14} /> },
    { key: 'system', label: 'System', icon: <Monitor size={14} /> },
  ];

  return (
    <div className="settings-popover absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-2rem)] solid-popover shadow-2xl shadow-black/60 z-[100] border border-mentor-border/90" style={{ borderRadius: 16 }}>
      <div className="popover-header flex items-center justify-between p-4 border-b border-mentor-border/60 bg-slate-950/30">
        <div className="flex items-center gap-2">
          <Settings size={16} className="text-mentor-accent" />
          <h3 className="font-display font-bold text-sm text-mentor-text">Settings</h3>
          {savedFlash && <span className="inline-flex items-center gap-1 text-[10px] text-signal-green font-display font-bold"><Check size={12} /> Saved</span>}
        </div>
        <button onClick={onClose} className="popover-close text-mentor-muted hover:text-mentor-text transition-colors"><X size={16} /></button>
      </div>

      <div className="p-4 space-y-5">
        <div>
          <label className="text-xs font-display font-semibold text-mentor-muted uppercase tracking-wider block mb-2">Theme</label>
          <div className="grid grid-cols-3 gap-2">
            {themeOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => updateSetting('theme', option.key)}
                className={`theme-option ${settings.theme === option.key ? 'is-active' : ''} rounded-xl border px-3 py-2 text-xs font-display font-bold flex items-center justify-center gap-1.5 transition-all ${settings.theme === option.key ? 'border-mentor-accent/70 bg-mentor-accent/15 text-mentor-accent' : 'border-mentor-border/60 bg-slate-950/25 text-mentor-text hover:border-mentor-accent/40'}`}
              >
                {option.icon}{option.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-mentor-muted mt-2 leading-relaxed">Dark is the classic Bond Mentor view. Light is easier in bright rooms. System follows your device.</p>
        </div>

        <div className="space-y-2">
          <SettingToggle
            enabled={settings.soundEnabled}
            onChange={(value) => updateSetting('soundEnabled', value)}
            label="Alert Sounds"
            description="Allows future signal-change alerts to play a soft sound. Visual alerts still show when this is off."
            icon={settings.soundEnabled ? <Volume2 size={15} className="text-mentor-accent mt-0.5" /> : <VolumeX size={15} className="text-mentor-muted mt-0.5" />}
          />
          <SettingToggle
            enabled={settings.desktopNotifications}
            onChange={(value) => updateSetting('desktopNotifications', value)}
            label="Desktop Notifications"
            description="Keeps the preference ready for browser notifications once enabled."
            icon={<Bell size={15} className="text-mentor-accent mt-0.5" />}
          />
          <SettingToggle
            enabled={settings.signalChangeAlerts}
            onChange={(value) => updateSetting('signalChangeAlerts', value)}
            label="Signal Change Alerts"
            description="Prioritizes confirmed signal, site mode, and data-confidence changes in the notification drawer."
            icon={<Shield size={15} className="text-mentor-accent mt-0.5" />}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-display font-semibold text-mentor-muted uppercase tracking-wider block mb-1.5">Data Date Format</label>
            <select value={settings.dataDateFormat} onChange={e => updateSetting('dataDateFormat', e.target.value)}
              className="settings-select w-full px-3 py-2 rounded-lg text-sm font-body bg-mentor-bg border border-mentor-border/70 text-mentor-text focus:outline-none focus:border-mentor-accent/50">
              <option value="MM-DD-YYYY">MM-DD-YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              <option value="Month Day, Year">Month Day, Year</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-display font-semibold text-mentor-muted uppercase tracking-wider block mb-1.5">Time Format</label>
            <select value={settings.timeFormat} onChange={e => updateSetting('timeFormat', e.target.value)}
              className="settings-select w-full px-3 py-2 rounded-lg text-sm font-body bg-mentor-bg border border-mentor-border/70 text-mentor-text focus:outline-none focus:border-mentor-accent/50">
              <option value="12h">12-Hour</option>
              <option value="24h">24-Hour</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-display font-semibold text-mentor-muted uppercase tracking-wider block mb-1.5">Mission Control Sections</label>
          <select value={settings.missionControlLayout} onChange={e => updateSetting('missionControlLayout', e.target.value)}
            className="settings-select w-full px-3 py-2 rounded-lg text-sm font-body bg-mentor-bg border border-mentor-border/70 text-mentor-text focus:outline-none focus:border-mentor-accent/50">
            <option value="expanded">Expanded by default</option>
            <option value="collapsed">Collapsed by default</option>
            <option value="remember">Remember my choices</option>
          </select>
          <p className="text-[11px] text-mentor-muted mt-2 leading-relaxed">Choose how the collapsible Mission Control panels open. Expanded shows everything up front; Remember keeps whatever you last opened or closed.</p>
        </div>

        <div className="space-y-2">
          <SettingToggle
            enabled={settings.showBeginnerNotes}
            onChange={(value) => updateSetting('showBeginnerNotes', value)}
            label="Show Beginner Notes"
            description="Keeps plain-English helper notes visible near filters and advanced sections."
            icon={<Check size={15} className="text-mentor-accent mt-0.5" />}
          />
          <SettingToggle
            enabled={settings.rememberCollapsedSections}
            onChange={(value) => updateSetting('rememberCollapsedSections', value)}
            label="Remember Collapsed Sections"
            description="Stores your expand/collapse preferences in this browser."
            icon={<Check size={15} className="text-mentor-accent mt-0.5" />}
          />
          <SettingToggle
            enabled={settings.compactAdminPanels}
            onChange={(value) => updateSetting('compactAdminPanels', value)}
            label="Compact Admin Panels"
            description="Saves the preference for denser admin layouts as analytics grows."
            icon={<Settings size={15} className="text-mentor-accent mt-0.5" />}
          />
        </div>
      </div>

      <div className="p-3 border-t border-mentor-border/60 bg-slate-950/25">
        <p className="text-[10px] text-mentor-muted text-center font-body">
          {isLoggedIn
            ? 'Preferences are saved to your account and follow you across devices.'
            : 'Preferences are saved in this browser. Sign in to sync them across devices.'}
          {' '}Server secrets, API keys, and production mode still belong in the private .env file.
        </p>
      </div>
    </div>
  );
}

/* ============================================
   MAIN HEADER
   ============================================ */
export default function Header({ user, onOpenAccount, onLogout, liveData, masterSignal, signalHistory, siteMode, auctionWatch }) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [readNotifications, setReadNotifications] = useState(() => readJson(READ_NOTIFICATIONS_KEY, []));
  const [hiddenNotifications, setHiddenNotifications] = useState(() => readJson(HIDDEN_NOTIFICATIONS_KEY, []));
  const [notificationHistory, setNotificationHistory] = useState(() => readJson(NOTIFICATION_HISTORY_KEY, []));
  const [notificationView, setNotificationView] = useState('inbox');
  const [notificationFlash, setNotificationFlash] = useState('');
  const { settings, updateSetting, savedFlash } = useBondMentorSettings(user);
  const now = new Date();

  const rawNotifications = useMemo(() => {
    return buildLiveNotifications({ liveData, masterSignal, signalHistory, siteMode, user, auctionWatch })
      .filter((item) => settings.signalChangeAlerts || !['Master Signal', 'Latest Confirmed', 'Signal History'].includes(item.type));
  }, [liveData, masterSignal, signalHistory, siteMode, user, auctionWatch, settings.signalChangeAlerts]);

  useEffect(() => {
    const remembered = rememberNotificationHistory(rawNotifications);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate data fetch / one-time init on mount
    setNotificationHistory(remembered);
  }, [rawNotifications]);

  const notifications = useMemo(() => {
    const readSet = new Set(readNotifications);
    const hiddenSet = new Set(hiddenNotifications);
    return rawNotifications
      .filter((item) => !hiddenSet.has(item.id))
      .map((item) => ({ ...item, read: readSet.has(item.id) }));
  }, [rawNotifications, readNotifications, hiddenNotifications]);

  const recentNotifications = useMemo(() => {
    const readSet = new Set(readNotifications);
    return notificationHistory.map((item) => ({ ...item, read: readSet.has(item.id) }));
  }, [notificationHistory, readNotifications]);

  const unreadCount = notifications.filter((item) => !item.read).length;

  // Arm the audio unlock once: the first real user interaction (click/key/tap)
  // quietly enables alert sounds, satisfying the browser's autoplay policy.
  useEffect(() => { installAudioUnlock(); }, []);

  // Sound the alert chime when a brand-new notification arrives — but only if
  // the user has Alert Sounds on, and never on the very first render (so the
  // page doesn't beep just for loading). We compare against the previous unread
  // count and against the set of IDs we've already chimed for.
  const prevUnreadRef = useRef(null);
  const chimedIdsRef = useRef(new Set());
  useEffect(() => {
    const currentUnreadIds = notifications.filter((item) => !item.read).map((item) => item.id);
    if (prevUnreadRef.current === null) {
      // First render: remember what's already here, don't sound anything.
      prevUnreadRef.current = unreadCount;
      currentUnreadIds.forEach((id) => chimedIdsRef.current.add(id));
      return;
    }
    const freshIds = currentUnreadIds.filter((id) => !chimedIdsRef.current.has(id));
    if (freshIds.length && settings.soundEnabled) {
      const hasWarning = notifications.some(
        (item) => freshIds.includes(item.id) && /red|danger|maintenance/i.test(`${item.severity} ${item.type}`)
      );
      playAlertSound(hasWarning ? 'warning' : 'alert');
    }
    freshIds.forEach((id) => chimedIdsRef.current.add(id));
    prevUnreadRef.current = unreadCount;
  }, [notifications, unreadCount, settings.soundEnabled]);


  function showNotificationFlash(message) {
    setNotificationFlash(message);
    window.clearTimeout(window.__bondMentorNotificationFlashTimer);
    window.__bondMentorNotificationFlashTimer = window.setTimeout(() => setNotificationFlash(''), 1700);
  }

  function markAllRead() {
    const ids = Array.from(new Set([...readNotifications, ...notifications.map((item) => item.id)]));
    setReadNotifications(ids);
    writeJson(READ_NOTIFICATIONS_KEY, ids);
    setNotificationView('inbox');
    showNotificationFlash('All notifications marked as read.');
  }

  function clearFromView() {
    const ids = Array.from(new Set([...hiddenNotifications, ...notifications.map((item) => item.id)]));
    const readIds = Array.from(new Set([...readNotifications, ...notifications.map((item) => item.id)]));
    setHiddenNotifications(ids);
    setReadNotifications(readIds);
    writeJson(HIDDEN_NOTIFICATIONS_KEY, ids);
    writeJson(READ_NOTIFICATIONS_KEY, readIds);
    setNotificationView('inbox');
    showNotificationFlash('Notifications cleared from your inbox.');
  }

  function restoreNotification(id) {
    const ids = hiddenNotifications.filter((itemId) => itemId !== id);
    setHiddenNotifications(ids);
    writeJson(HIDDEN_NOTIFICATIONS_KEY, ids);
    setNotificationView('inbox');
    showNotificationFlash('Notification restored to your inbox.');
  }

  function restoreAllNotifications() {
    setHiddenNotifications([]);
    writeJson(HIDDEN_NOTIFICATIONS_KEY, []);
    setNotificationView('inbox');
    showNotificationFlash('Notifications restored to your inbox.');
  }

  // Close panels when clicking outside
  const nRef = useRef(null);
  const sRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (nRef.current && !nRef.current.contains(e.target)) setShowNotifications(false);
      if (sRef.current && !sRef.current.contains(e.target)) setShowSettings(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header className="bond-header sticky top-0 z-[70] border-b border-mentor-border/60 bg-mentor-surface/95 backdrop-blur-xl shadow-lg shadow-black/20 isolate">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-display font-bold text-xl text-mentor-text tracking-tight">
                Bond <span className="text-gradient">Mentor</span>
              </h1>
              <p className="text-xs text-mentor-muted font-body">Smart Money Decoder</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm text-mentor-text font-mono">{formatDate(now)}</p>
              <p className="text-xs text-mentor-muted font-mono">{formatTime(now)} — {getGreeting()}</p>
            </div>

            {/* Notifications */}
            <div className="relative" ref={nRef}>
              <button
                onClick={() => { setShowNotifications(!showNotifications); setShowSettings(false); }}
                className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all relative ${showNotifications ? 'border-mentor-accent/50 text-mentor-accent bg-mentor-accent/10' : 'border-mentor-border/50 text-mentor-muted hover:text-mentor-text hover:border-mentor-border'}`}
                title="Open notifications"
              >
                <Bell size={16} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-signal-red text-white text-[9px] font-bold flex items-center justify-center">{unreadCount}</span>
                )}
              </button>
              {showNotifications && (
                <NotificationPanel
                  notifications={notifications}
                  recentNotifications={recentNotifications}
                  hiddenIds={hiddenNotifications}
                  notificationView={notificationView}
                  flashMessage={notificationFlash}
                  onClose={() => setShowNotifications(false)}
                  onMarkAllRead={markAllRead}
                  onClearFromView={clearFromView}
                  onShowInbox={() => setNotificationView('inbox')}
                  onShowRecent={() => setNotificationView('recent')}
                  onRestoreOne={restoreNotification}
                  onRestoreAll={restoreAllNotifications}
                />
              )}
            </div>

            {/* Account */}
            {user && (
              <button
                type="button"
                onClick={onOpenAccount}
                className="hidden md:flex items-center gap-2 rounded-lg border border-mentor-border/50 px-3 py-2 text-left hover:border-mentor-accent/50 hover:bg-mentor-accent/10 transition-all"
                title="Open account"
              >
                <UserCircle size={16} className="text-mentor-accent" />
                <div className="leading-tight">
                  <p className="text-xs font-display font-bold text-mentor-text max-w-[150px] truncate">{user.name || user.email}</p>
                  <p className="text-[10px] uppercase tracking-wider text-mentor-muted">{user.roleLabel || user.role}</p>
                </div>
              </button>
            )}

            {/* Settings */}
            <div className="relative" ref={sRef}>
              <button
                onClick={() => { setShowSettings(!showSettings); setShowNotifications(false); }}
                className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${showSettings ? 'border-mentor-accent/50 text-mentor-accent bg-mentor-accent/10' : 'border-mentor-border/50 text-mentor-muted hover:text-mentor-text hover:border-mentor-border'}`}
                title="Open display settings"
              >
                <Settings size={16} />
              </button>
              {showSettings && (
                <SettingsPanel
                  onClose={() => setShowSettings(false)}
                  settings={settings}
                  updateSetting={updateSetting}
                  savedFlash={savedFlash}
                  isLoggedIn={Boolean(user)}
                />
              )}
            </div>

            {user && (
              <button
                type="button"
                onClick={onLogout}
                className="w-9 h-9 rounded-lg border border-mentor-border/50 text-mentor-muted hover:text-signal-red hover:border-signal-red/50 flex items-center justify-center transition-all"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
